<template>
  <!-- display:contents so this wrapper does not change the drawer/dialog flex layout -->
  <div class="bulk-upload-frame">
    <input
      ref="reuploadInput"
      type="file"
      class="bulk-import-dialog__hidden-input"
      :accept="fileAccept"
      tabindex="-1"
      aria-hidden="true"
      :data-testid="testIds.reuploadInput"
      @change="onReuploadFileSelected"
    />

    <div
      class="bulk-import-dialog"
      :class="{ 'bulk-import-dialog--preview': hasPreview }"
      :data-testid="testIds.body"
    >
      <section class="bulk-import-dialog__step" :data-testid="testIds.step1">
        <h3 class="bulk-import-dialog__step-title">Step 1: Download Template</h3>
        <p class="bulk-import-dialog__step-desc">{{ step1Description }}</p>
        <button
          type="button"
          class="bulk-import-dialog__download-link"
          :data-testid="testIds.download"
          @click="$emit('download')"
        >
          <img src="/img/icons/DownloadIcon.svg" alt="" aria-hidden="true" />
          <span>Download template</span>
        </button>
      </section>

      <section
        class="bulk-import-dialog__step bulk-import-dialog__step--upload"
        :data-testid="testIds.step2"
      >
        <div class="bulk-import-dialog__step-header">
          <div>
            <h3 class="bulk-import-dialog__step-title">{{ step2Title }}</h3>
            <p v-if="!hasPreview" class="bulk-import-dialog__step-desc">
              {{ step2Description }}
            </p>
          </div>
          <v-btn
            v-if="showReupload"
            color="white"
            class="bulk-import-dialog__reupload-btn secondaryBtn d-flex align-center"
            :data-testid="testIds.reuploadButton"
            @click="$emit('reupload')"
          >
            <v-icon class="pr-1">mdi-reload</v-icon>
            <span>Re-Upload</span>
          </v-btn>
        </div>

        <div
          v-if="validating"
          class="bulk-import-dialog__validating"
          :data-testid="testIds.validating"
        >
          <v-progress-circular indeterminate color="primary" size="32" width="3" />
          <p class="bulk-import-dialog__validating-text">Validating file…</p>
          <p
            v-if="showSelectedFileName && selectedFileName"
            class="bulk-import-dialog__validating-file"
          >
            {{ selectedFileName }}
          </p>
        </div>

        <div
          v-else-if="showInlineErrors"
          class="bulk-import-dialog__inline-errors"
          :data-testid="testIds.inlineErrors"
        >
          <p
            class="bulk-import-dialog__inline-errors-text"
            :data-testid="testIds.inlineErrorMessage"
          >
            {{ inlineErrorText }}
            <span v-if="inlineErrorNote">{{ inlineErrorNote }}</span>
          </p>
          <div
            v-if="headerErrorMessages.length"
            class="mt-2"
            :data-testid="testIds.headerErrors"
          >
            <strong>Header Issues Detected:</strong>
            <div
              class="mt-1 pr-1"
              :style="headerErrorMessages.length > 2 ? 'max-height:70px; overflow-y:auto; scrollbar-width:thin;' : ''"
              :data-testid="testIds.headerErrorsList"
            >
              <div
                v-for="(msg, index) in headerErrorMessages"
                :key="'header-' + index"
                class="mb-1"
                :data-testid="indexedId(testIds.headerError, index)"
              >
                - {{ msg }}
              </div>
            </div>
          </div>
          <div
            v-if="rowErrors.length > 0 || dataErrorMessages.length > 0"
            class="mt-2"
            :data-testid="testIds.rowErrors"
          >
            <strong>Data Errors:</strong>
            <div class="mt-1 pr-1">
              <div
                v-for="(errorObj, index) in rowErrors"
                :key="'row-' + index"
                class="mb-1"
                :data-testid="indexedId(testIds.rowError, index)"
              >
                <div
                  v-for="(msg, i) in Object.values(errorObj)"
                  :key="'msg-' + index + '-' + i"
                  class="mb-1 noTransform"
                  :data-testid="indexedId(testIds.rowErrorMessage, index, i)"
                >
                  {{ `Row ${index + 1}: ` + (msg || '').trim() || `Error in row ${index + 1}` }}
                </div>
              </div>
              <div
                v-for="(msg, index) in dataErrorMessages"
                :key="'data-msg-' + index"
                class="mb-1 noTransform"
                :data-testid="indexedId(testIds.dataError || testIds.rowError, index)"
              >
                - {{ msg }}
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="!hasPreview"
          class="bulk-import-dialog__dropzone"
          :data-testid="testIds.dropzone"
        >
          <DragDropfile
            ref="dragDrop"
            v-bind="dropzoneAttrs"
            :files="file"
            :reset-trigger="resetTrigger"
            :data-testid="testIds.dragDrop"
            @update:files="$emit('files-change', $event)"
          />
        </div>

        <div
          v-else
          class="bulk-import-dialog__preview"
          :data-testid="testIds.preview"
        >
          <slot name="preview" />

          <div
            v-if="showPreviewFooter"
            class="bulk-import-dialog__preview-footer"
            :data-testid="testIds.errorFooter"
          >
            <div class="bulk-import-dialog__preview-footer-errors">
              <p
                v-if="showErrorFooter"
                class="bulk-import-dialog__error-footer bulk-import-dialog__error-footer--inline"
                :data-testid="testIds.errorFooterMessage"
              >
                {{ errorFooterText }}
                <span v-if="errorFooterNote">{{ errorFooterNote }}</span>
              </p>
              <div
                v-if="summaryErrorMessages.length"
                class="bulk-import-dialog__summary-errors"
                :data-testid="testIds.summaryErrors"
              >
                <div
                  v-for="(err, idx) in summaryErrorMessages"
                  :key="'summary-err-' + idx"
                  class="error--text font-size-xm"
                >
                  {{ summaryLabel(idx, err) }}
                </div>
              </div>
            </div>
            <div
              v-if="showErrorFilter"
              class="bulk-import-dialog__show-errors bulk-import-dialog__show-errors--footer"
              :data-testid="testIds.errorFilterRow"
            >
              <v-checkbox
                :input-value="showErrorsOnly"
                color="caccounting"
                hide-details
                dense
                class="ma-0 pa-0"
                :data-testid="testIds.errorFilter"
                @change="$emit('update:showErrorsOnly', $event)"
              >
                <template v-slot:label>
                  <span>Show only items with error</span>
                </template>
              </v-checkbox>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import DragDropfile from '../../../../views/Inputs/DragDropfile.vue';

/**
 * Shared bulk-upload shell used by BulkUpload.vue: Step 1 download template,
 * Step 2 drop zone / preview slot, validating overlay, and error footer.
 * Does not parse files or upload — BulkUpload (or an external host via slots)
 * owns that. Preview columns come from the #preview slot.
 */
export default {
  name: 'BulkUploadFrame',
  components: {
    DragDropfile,
  },
  props: {
    testIds: {
      type: Object,
      default: () => ({}),
    },
    step1Description: {
      type: String,
      default: '',
    },
    step2Title: {
      type: String,
      default: 'Step 2: Upload File',
    },
    step2Description: {
      type: String,
      default: '',
    },
    hasPreview: {
      type: Boolean,
      default: false,
    },
    validating: {
      type: Boolean,
      default: false,
    },
    selectedFileName: {
      type: String,
      default: '',
    },
    showSelectedFileName: {
      type: Boolean,
      default: true,
    },
    showReupload: {
      type: Boolean,
      default: false,
    },
    file: {
      type: [Array, Object, File],
      default: null,
    },
    resetTrigger: {
      type: Boolean,
      default: false,
    },
    fileAccept: {
      type: String,
      default: '.xlsx,.xls,.xlsm,.xlsb,.csv',
    },
    hideDropzoneDetails: {
      type: Boolean,
      default: true,
    },
    singleFile: {
      type: Boolean,
      default: false,
    },
    dropzoneAccept: {
      type: String,
      default: '',
    },
    showInlineErrors: {
      type: Boolean,
      default: false,
    },
    inlineErrorText: {
      type: String,
      default: '',
    },
    inlineErrorNote: {
      type: String,
      default: '',
    },
    headerErrorMessages: {
      type: Array,
      default: () => [],
    },
    rowErrors: {
      type: Array,
      default: () => [],
    },
    /** Plain string list under Data Errors (no automatic "Row N:" prefix). */
    dataErrorMessages: {
      type: Array,
      default: () => [],
    },
    showPreviewFooter: {
      type: Boolean,
      default: false,
    },
    showErrorFooter: {
      type: Boolean,
      default: false,
    },
    errorFooterText: {
      type: String,
      default: '',
    },
    errorFooterNote: {
      type: String,
      default: '',
    },
    summaryErrorMessages: {
      type: Array,
      default: () => [],
    },
    /** Bulk edit prints "1.Message" with no space after the period. */
    summaryTight: {
      type: Boolean,
      default: false,
    },
    showErrorFilter: {
      type: Boolean,
      default: false,
    },
    showErrorsOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dropzoneAttrs() {
      const attrs = {};
      if (this.hideDropzoneDetails) attrs.hidedetails = true;
      if (this.singleFile) attrs.multiplefiles = false;
      if (this.dropzoneAccept) attrs.accept = this.dropzoneAccept;
      return attrs;
    },
  },
  methods: {
    indexedId(prefix, index, childIndex) {
      if (!prefix) return undefined;
      if (childIndex === undefined) return `${prefix}-${index}`;
      return `${prefix}-${index}-${childIndex}`;
    },
    summaryLabel(index, message) {
      const separator = this.summaryTight ? '.' : '. ';
      return `${index + 1}${separator}${message}`;
    },
    /** Opens the hidden file input used by Re-Upload. */
    openFilePicker() {
      const input = this.$refs.reuploadInput;
      if (!input) return;
      input.value = '';
      input.click();
    },
    onReuploadFileSelected(event) {
      this.$emit('reupload-file', event);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './BulkUpload.scss';

.bulk-upload-frame {
  display: contents;
}
</style>
